/**
 * SEO + Analytics head injector + conversion tracking.
 * Runs at App mount + on every route change. All configuration is read from
 * env vars so the user only has to drop values into /app/frontend/.env:
 *   REACT_APP_GA_MEASUREMENT_ID          — Google Analytics 4 (e.g. G-XXXXXXXXXX)
 *   REACT_APP_GOOGLE_SITE_VERIFICATION   — Search Console verification token
 *
 * Fails gracefully: if either env var is missing, the corresponding integration
 * silently disables so deployments are never blocked.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CONSULTATION_URL } from "@/data/constants";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const GSC_TOKEN = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "";

// Any external booking domain we treat as a consult booking action.
// CONSULTATION_URL is the source of truth (currently Cal.com).
let CONSULT_HOSTNAME = "";
try { CONSULT_HOSTNAME = new URL(CONSULTATION_URL).hostname; } catch { /* noop */ }

// Search Console meta tag is now baked into `public/index.html` at build
// time via CRA's `%REACT_APP_GOOGLE_SITE_VERIFICATION%` substitution. That
// means the tag is present in the raw HTML response — which is what Google's
// verifier fetches (it does NOT execute JavaScript). We keep a runtime
// safety-net here that (a) removes the empty placeholder tag when the env
// var was unset at build time, and (b) injects a tag if HTML baking is
// somehow missing on this deployment.
const ensureSearchConsoleTag = () => {
  const existing = document.querySelector('meta[name="google-site-verification"]');
  if (existing) {
    const content = existing.getAttribute("content") || "";
    // Strip empty/placeholder tags so we don't ship `content=""` to crawlers.
    if (!content || content === "%REACT_APP_GOOGLE_SITE_VERIFICATION%") {
      if (GSC_TOKEN) {
        existing.setAttribute("content", GSC_TOKEN);
      } else {
        existing.remove();
      }
    }
    return;
  }
  if (!GSC_TOKEN) return;
  const m = document.createElement("meta");
  m.name = "google-site-verification";
  m.content = GSC_TOKEN;
  document.head.appendChild(m);
};

// GA4 loader: injects gtag.js exactly once, then wires window.gtag(...) so
// downstream route changes just fire config events instead of re-injecting.
// Respects the cookie consent choice — if the visitor picked "Essential Only"
// (or hasn't yet decided) we hold off on loading gtag.js entirely.
const _consentAllowsAnalytics = () => {
  if (typeof window === "undefined") return false;
  try {
    const c = window.localStorage.getItem("flowtalis_cookie_consent");
    return c === "accepted";
  } catch { return false; }
};

const ensureGA4Loaded = () => {
  if (!GA_ID) return false;
  if (!_consentAllowsAnalytics()) return false;
  if (window.__ga4Loaded) return true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view:false — we fire it ourselves on route change so SPA nav
  // is captured accurately (default gtag only fires once on hard load).
  window.gtag("config", GA_ID, { send_page_view: false });
  window.__ga4Loaded = true;
  return true;
};

// Retry loading GA4 whenever the visitor accepts cookies mid-session.
if (typeof window !== "undefined" && !window.__ga4ConsentBound) {
  window.__ga4ConsentBound = true;
  window.addEventListener("storage", () => ensureGA4Loaded());
  window.addEventListener("flowtalis:cookie-consent", (e) => {
    if (e && e.detail === "accepted") ensureGA4Loaded();
  });
}

// Global click delegation: any anchor pointing at the Cal.com booking URL is
// treated as an intent-to-book conversion. Attached ONCE, at the document
// level, so we never have to sprinkle onClick handlers across every CTA.
const ensureConsultDelegation = () => {
  if (window.__consultClickBound) return;
  window.__consultClickBound = true;
  document.addEventListener("click", (e) => {
    if (!window.gtag || !CONSULT_HOSTNAME) return;
    const a = e.target && e.target.closest && e.target.closest("a[href]");
    if (!a) return;
    let host = "";
    try { host = new URL(a.href, window.location.href).hostname; } catch { return; }
    if (host !== CONSULT_HOSTNAME) return;
    trackConversion("consult_booked", {
      cta_label: (a.textContent || "").trim().slice(0, 80),
      cta_href: a.href,
    });
  }, true);
};

/** React hook: mount once at the top of App.js. */
export default function useSiteAnalytics() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    ensureSearchConsoleTag();
    ensureGA4Loaded();
    ensureConsultDelegation();
  }, []);
  useEffect(() => {
    if (!GA_ID || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);
}

/**
 * Fire a GA4 conversion event with sensible auto-params merged in.
 * Silent no-op when GA4 is not configured — this keeps the surface safe to
 * call from any component without guards.
 *
 * Auto-included params (unless overridden by caller):
 *   page_location, page_title, page_path, referrer
 */
export function trackConversion(name, params = {}) {
  if (!window.gtag) return;
  const auto = {
    page_location: window.location.href,
    page_title: document.title,
    page_path: window.location.pathname + window.location.search,
    referrer: document.referrer || undefined,
  };
  window.gtag("event", name, { ...auto, ...params });
}

/** Legacy alias — kept for older callers that already import trackEvent. */
export const trackEvent = trackConversion;

export const analyticsConfig = {
  ga4_enabled: !!GA_ID,
  ga4_id: GA_ID || null,
  gsc_enabled: !!GSC_TOKEN,
};
